import React, { useMemo } from "react";
import { Autocomplete, Chip, TextField, TextFieldProps } from "@mui/material";
import { useTranslation } from "react-i18next";
import { LANGUAGE_OPTIONS, LanguageOption } from "../../utils/languageUtils";

interface LanguageSelectProps {
  value: string | string[];
  onChange: (value: string | string[]) => void;
  label?: string;
  error?: boolean;
  helperText?: React.ReactNode;
  required?: boolean;
  size?: TextFieldProps["size"];
  multiple?: boolean;
}

export const LanguageSelect: React.FC<LanguageSelectProps> = ({
  value,
  onChange,
  label = "Language",
  error,
  helperText,
  required,
  size = "small",
  multiple = false,
}) => {
  const { t } = useTranslation();

  const selectedOptions = useMemo(() => {
    if (multiple && Array.isArray(value)) {
      return LANGUAGE_OPTIONS.filter((opt) => value.includes(opt.value));
    }
    if (!multiple && typeof value === "string") {
      return LANGUAGE_OPTIONS.find((opt) => opt.value === value) || null;
    }
    return multiple ? [] : null;
  }, [value, multiple]);

  const handleChange = (
    _: unknown,
    newValue: LanguageOption | LanguageOption[] | null
  ) => {
    if (multiple) {
      const selected = newValue as LanguageOption[];

      // If the user just added "Any", keep only "Any"
      const hadAny = Array.isArray(value) && value.includes("ANY");
      const hasAny = selected.some((o) => o.value === "ANY");
      if (hasAny && !hadAny) {
        onChange(["ANY"]);
        return;
      }

      // If "Any" was already selected and user picks something else, remove "Any"
      const values = hadAny
        ? selected.filter((o) => o.value !== "ANY").map((o) => o.value)
        : selected.map((o) => o.value);

      onChange(values);
    } else {
      onChange(newValue ? (newValue as LanguageOption).value : "");
    }
  };

  return (
    <Autocomplete
      multiple={multiple}
      options={LANGUAGE_OPTIONS}
      getOptionLabel={(option) =>
        option.value === "ANY" ? t("common.any", "Any") : option.label
      }
      value={selectedOptions}
      onChange={handleChange}
      renderTags={(tagValue, getTagProps) =>
        (tagValue as LanguageOption[]).map((option, index) => (
          <Chip
            label={
              option.value === "ANY" ? t("common.any", "Any") : option.label
            }
            {...getTagProps({ index })}
            key={option.value}
            color={option.value === "ANY" ? "primary" : "default"}
            size="small"
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          error={error}
          helperText={helperText}
          required={required}
          size={size}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
            },
          }}
        />
      )}
    />
  );
};
